import { Bot, KeyRound, LayoutGrid, LogOut, Menu } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import FullscreenToggle from './FullscreenToggle';
import ThemePicker from './ThemePicker';
import { useMobileNav } from '../context/MobileNavContext';

type AppShellProps = {
    children: ReactNode;
};

type NavItem = {
    href: string;
    label: string;
    description: string;
    icon: LucideIcon;
};

const NAV_ITEMS: NavItem[] = [
    { href: '/', label: 'Dashboard', description: 'Dashboard', icon: LayoutGrid },
    { href: '/programs', label: 'Programs', description: 'Programs & backtest results', icon: Bot },
];

function matchesPrefix(pathname: string, prefix: string) {
    return prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export default function AppShell({ children }: AppShellProps) {
    const { pathname } = useLocation();
    const currentSection = NAV_ITEMS.find((item) => matchesPrefix(pathname, item.href)) ?? NAV_ITEMS[0];
    const { isOpen: isMobileOpen, setIsOpen: setIsMobileOpen } = useMobileNav();
    const railBorder = { borderColor: 'color-mix(in srgb, var(--card-border) 72%, transparent)' };
    const isAccountActive = matchesPrefix(pathname, '/account');

    return (
        <div className="h-screen min-h-screen overflow-hidden">
            {isMobileOpen ? (
                <button
                    type="button"
                    aria-label="Close menu"
                    className="fixed inset-0 z-30 bg-black/30 lg:hidden"
                    onClick={() => setIsMobileOpen(false)}
                />
            ) : null}

            <div className="flex h-full w-full">
                {/* Mobile nav drawer. Desktop gets the slim icon rail below instead. */}
                <aside
                    className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col overflow-y-auto border-r bg-[var(--background)] px-2 py-4 transition-transform duration-200 lg:hidden ${
                        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                    style={railBorder}
                >
                    <Link to="/" className="flex items-center gap-3 rounded-[24px] px-1 py-2">
                        <span
                            className="flex h-11 w-11 items-center justify-center rounded-2xl text-base font-semibold"
                            style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent)' }}
                        >
                            A
                        </span>
                        <span>
                            <span className="block text-lg font-semibold leading-none">AlgoX</span>
                            <span className="mt-1 block text-xs" style={{ color: 'var(--foreground-muted)' }}>
                                Trading workspace
                            </span>
                        </span>
                    </Link>

                    <nav className="mt-6 space-y-1">
                        {NAV_ITEMS.map((item) => {
                            const active = matchesPrefix(pathname, item.href);
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setIsMobileOpen(false)}
                                    className="flex items-center gap-3 rounded-full px-3 py-2.5"
                                    style={active ? { backgroundColor: 'var(--accent-tint)', color: 'var(--accent)' } : { color: 'var(--foreground-muted)' }}
                                >
                                    <span
                                        className="flex h-9 w-9 items-center justify-center rounded-full"
                                        style={active ? { backgroundColor: 'color-mix(in srgb, var(--background-elevated) 92%, transparent)' } : undefined}
                                    >
                                        <Icon className="h-5 w-5" aria-hidden="true" color={active ? 'var(--accent)' : 'var(--foreground-muted)'} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium text-inherit">{item.label}</span>
                                        <span className="block text-xs text-inherit opacity-80">{item.description}</span>
                                    </span>
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="mt-auto space-y-2 px-1 pt-6">
                        <Link
                            to="/account"
                            onClick={() => setIsMobileOpen(false)}
                            className="button-ghost flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium"
                        >
                            <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
                            Account
                        </Link>
                        <Link to="/logout" className="button-ghost flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium">
                            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
                            Sign out
                        </Link>
                    </div>
                </aside>

                {/* Desktop icon rail — fixed width, never collapses. */}
                <nav
                    className="hidden w-[60px] flex-none flex-col items-center gap-1.5 border-r py-3.5 lg:flex"
                    style={railBorder}
                    aria-label="Site navigation"
                >
                    <Link
                        to="/"
                        className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl text-sm font-semibold"
                        style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent)' }}
                        title="AlgoX · Trading workspace"
                    >
                        A
                    </Link>
                    {NAV_ITEMS.map((item) => {
                        const active = matchesPrefix(pathname, item.href);
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                title={item.label}
                                aria-label={item.label}
                                className={`icon-rail-item flex h-11 w-full items-center justify-center ${active ? 'is-active' : ''}`}
                            >
                                <Icon className="h-5 w-5" aria-hidden="true" color={active ? 'var(--accent)' : 'var(--foreground-muted)'} />
                            </Link>
                        );
                    })}

                    <span className="flex-1" />

                    <Link
                        to="/account"
                        title="Account"
                        aria-label="Account"
                        className={`icon-rail-item flex h-11 w-full items-center justify-center ${isAccountActive ? 'is-active' : ''}`}
                    >
                        <KeyRound className="h-[18px] w-[18px]" style={{ color: isAccountActive ? 'var(--accent)' : 'var(--foreground-muted)' }} aria-hidden="true" />
                    </Link>
                    <Link to="/logout" title="Sign out" aria-label="Sign out" className="icon-rail-item flex h-11 w-full items-center justify-center">
                        <LogOut className="h-[18px] w-[18px]" style={{ color: 'var(--foreground-muted)' }} aria-hidden="true" />
                    </Link>
                </nav>

                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <header className="shrink-0 px-4 py-3 sm:px-6 sm:py-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-2">
                                <button
                                    type="button"
                                    aria-label="Open menu"
                                    onClick={() => setIsMobileOpen(true)}
                                    className="flex h-10 w-10 items-center justify-center rounded-full lg:hidden"
                                    style={{ color: 'var(--foreground-muted)' }}
                                >
                                    <Menu className="h-5 w-5" aria-hidden="true" />
                                </button>
                                <h1 className="truncate text-xl font-semibold sm:text-2xl">{currentSection.description}</h1>
                            </div>

                            <div className="flex shrink-0 items-center gap-2">
                                <FullscreenToggle compact />
                                <ThemePicker compact />
                            </div>
                        </div>
                    </header>

                    <main className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">{children}</main>
                </div>
            </div>
        </div>
    );
}
